<template>
    <div class="app-content-builder-nav">
        <div class="app-content-builder-nav__header">
            <h3 class="app-content-builder-nav__header-title">
                <slot name="navigation"></slot>
            </h3>
            <p v-if="dirty"
                class="app-content-builder-nav__unsaved element-badge is--warning">
                You have unsaved changes
            </p> 
            <button
                @click="toggleInfo()"
                class="app-content-builder-nav__info element-chip is--info">
                <span v-if="isSyncing"
                    title="Sync in progress..."
                    class="element-badge is--processing has--circle">
                    Syncing
                </span>
                <span v-else-if="isSourceOutdated"
                    :title="`Last Sync: ${localizeDate(source.synced_at).format('dddd, MMMM Do YYYY, h:mm:ss a')}`"
                    class="element-badge is--error has--circle">
                    Out of Sync
                </span>
                <span v-else-if="source.synced_at"
                    class="element-badge is--active has--circle">
                    In Sync
                </span>
                Updates <span :class="['element-chip__trailing', unreadMessageCount ? 'is--warning' : 'is--alt']">{{ unreadMessageCount }}</span>
                Viewers <span :class="['element-chip__trailing', userCount > 1 ? 'is--error' : 'is--alt']">{{ userCount }}</span>
                <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
            </button>
            <div v-if="canPreview">
                <b-dropdown v-if="locales?.length" split @click="onPreviewPressed()" text="Preview" class="app-content-builder-nav__preview-dropdown element-button--secondary-dropdown">
                    <b-dropdown-item v-for="(locale, index) in locales"
                        :key="`locale-${index}`"
                        @click="onPreviewPressed(locale.locale)"
                        >{{ locale.language_code }}
                    </b-dropdown-item>
                </b-dropdown>
                <button v-else
                    @click="onPreviewPressed()"
                    class="element-button--secondary element-button">
                    Preview
                </button>
            </div>
            <button v-if="showGenerateButton"
                type="button"
                @click="generateMissingDatasets()"
                class="element-button--secondary element-button"
                id="generate-datasets-button">
                Generate Missing Datasets
            </button>
            <button
                @click="onSavePressed()"
                class="element-button element-button--primary"
                :disabled="!dirty || status">
                {{ status === 'saving' ? 'Saving...' : 'Save' }}
            </button>
            <button
                v-if="canSync"
                type="button"
                class="element-button element-button--primary"
                :disabled="status || isSyncing"
                @click="onSyncPressed()">
                {{ syncButtonLabel }}
            </button>
        </div>
        <div :class="['app-tooltip--log app-tooltip', { 'is--open': showInfo }]">
            <div class="app-tooltip__column">
                <h4 class="app-tooltip__title">Status Log</h4>
                <div class="app-tooltip__body">
                    <p v-if="source.updated_at"
                        :title="`${localizeDate(source.updated_at).format('dddd, MMMM Do YYYY, h:mm:ss a')}`">
                        <strong>Last Edited:</strong> <em>{{ localizeDate(source.updated_at).fromNow() }}</em>
                    </p>
                    <p v-if="source.synced_at"
                        class="mt-1"
                        :title="`${localizeDate(source.synced_at).format('dddd, MMMM Do YYYY, h:mm:ss a')}`">
                        <strong>Last Synced:</strong> <em>{{ localizeDate(source.synced_at).fromNow() }}</em>
                    </p>
                    <div ref="log-status">
                        <p>
                            <strong>Updates:</strong>
                            <em v-if="!timestamps.length" class="text-muted"> none available</em>
                        </p>
                        <code>
                            <ul v-for="time in timestamps"
                                :key="`time-${time}`">
                                <span class="timestamp">{{ formatTime(time) }}</span>
                                <li v-for="(message, index) in groupedUpdates[time]"
                                    :key="`message-${index}`"
                                    :class="[`text-${message.color}`]">
                                    {{ message.text }}
                                </li>
                            </ul>
                        </code>
                    </div>
                </div>
            </div>
            <div class="app-tooltip__column">
                <h4 class="app-tooltip__title">Current Viewers</h4>
                <div class="app-tooltip__body">
                    <ul>
                        <li v-for="user in users"
                            :key="`user-${user.id}`"
                            class="user-list-item">
                            {{ user.name }}
                            <span v-if="user.id === currentUser.id">(You)</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'
import api from '../api/builder'
import EventBus from '../helpers/event-bus'
import { localizeDate } from '../helpers/util'

var previewWindow

export default {
    components: { },
    props: { },
    data () {
        return {
            showInfo: false,
            unreadMessageCount: 0
        }
    },
    computed: {
        ...mapState('builder', {
            selectedId: state => state.selectedId,
            source: state => state.source,
            sourceType: state => state.sourceType,
            client: state => state.client,
            project: state => state.project,
            locales: state => state.project?.locales ?? [],
            status: state => state.status,
            users: state => state.users,
            screenTemplateMode: state => state.screenTemplateMode,
            statusLog: state => state.statusLog,
            isSyncing: state => state.isSyncing,
            dataObjects: state => state.dataObjects
        }),
        ...mapGetters('builder', [
            'dirty',
            'manifest'
        ]),
        currentUser () {
            return this.$store.state.user
        },
        userCount () {
            return this.users.length
        },
        previewUrl () {
            return `${this.source.preview_url}?go=${this.selectedId}&unlocked=1`
        },
        groupedUpdates () {
            return _.groupBy(this.statusLog, 'timestamp')
        },
        timestamps () {
            return _.keys(this.groupedUpdates).sort()
        },
        isIntegration () {
            return !!_.get(this.client, 'integration', false)
        },
        canPreview () {
            return _.get(this.source, 'preview_url')
        },
        canSync () {
            // has ingegration and not in screen template edit page
            return this.isIntegration && this.screenTemplateMode
        },
        syncButtonLabel () {
            return this.isSyncing ? 'Syncing to Portal...' : 'Sync to Portal'
        },
        isSourceOutdated () {
            // if synced_at not null, check for outdated flag
            return _.get(this.source, 'synced_at') ? _.get(this.source, 'outdated', false) : false;
        },
        showGenerateButton () {
            // Show if dataObjects is missing a dataset from the manifest and only on the Shared Data tab of Projects
            let manifest = this.manifest?.datasets?.required
            let manifestCopy = []
            let datasetCopy = []

            this.dataObjects.forEach(function(dataset) {
                datasetCopy.push(dataset.name)
            })

            manifest.forEach(function(manifestItem) {
                manifestCopy.push(manifestItem.name)
            })

            // TODO: change this logic, checking path doesn't make sense
            return window.location.pathname.includes('projects') && window.location.pathname.includes('content') && manifestCopy.length != datasetCopy.length
        }
    },
    watch: {
        async statusLog (value) {
            await this.$nextTick()
            this.$refs['log-status'].scrollTop = this.$refs['log-status'].scrollHeight
        },
    },
    methods: {
        localizeDate: localizeDate,
        async onSavePressed () {
            await this.$store.dispatch('builder/commitChanges')
        },
        async onSyncPressed () {
            this.$store.commit('builder/setStatus', 'syncing');
            if (this.sourceType === 'learning') {
                let result = await api.syncLearning(this.source.id)
            } else if (this.sourceType === 'project') {
                let result = await api.syncProject(this.source.id)
            }
            this.$store.commit('builder/setIsSyncing', true)
            this.$store.commit('builder/clearStatus');
        },
        onPreviewPressed (localeCode) {

            let previewUrl = this.previewUrl
            if (localeCode)
                previewUrl += '&locale='+localeCode

            if (previewWindow && !previewWindow.closed) {
                previewWindow.location.href = previewUrl
            } else {
                previewWindow = window.open(previewUrl, 'Preview')
            }
        },
        formatTime (ts) {
            return moment.unix(ts).format('LTS')
        },
        toggleInfo () {
            this.showInfo = !this.showInfo

            if (this.showInfo) {
                this.unreadMessageCount = 0
            }
        },
        async generateMissingDatasets () {
            try {
                let test = document.getElementById('generate-datasets-button')
                test.innerHTML = 'Generating datasets...'
                test.setAttribute('disabled', '')

                let result = await api.generateMissingDatasets(this.project.id)
                window.location.reload()
            } catch (err) {
                console.error(err)
            }
        }
    },
    created () {
        EventBus.$on('log:added', (notification) => {
            if (!this.showInfo) {
                this.unreadMessageCount++
            }
        })
    }
}
</script>
